import { LoadingPanel } from "@/components/LoadingPanel";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { formatDateGB } from "@/lib/format";
import { type Property, getSupabase } from "@/lib/supabase";
import { zodResolver } from "@hookform/resolvers/zod";
import { FileUp, Pencil, Plus, Save, Trash2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

type TenancyStatus = "upcoming" | "active" | "ended";

type TenancyRow = {
  id: string;
  property_id: string;
  tenant_user_id: string;
  start_date: string;
  end_date: string | null;
  monthly_rent_pence: number;
  status: TenancyStatus;
  hmf_tenant_id: number | null;
  property: { display_name: string } | null;
  tenant_profile: { full_name: string | null; contact_email: string | null } | null;
};

const formSchema = z.object({
  property_id: z.string().uuid("Pick a property"),
  tenant_first_name: z.string().trim().min(1).max(80),
  tenant_last_name: z.string().trim().min(1).max(80),
  tenant_email: z.string().trim().email("Enter a valid email"),
  tenant_phone: z.string().trim().max(20).optional().or(z.literal("")),
  start_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "YYYY-MM-DD"),
  end_date: z
    .string()
    .optional()
    .or(z.literal(""))
    .refine((v) => !v || /^\d{4}-\d{2}-\d{2}$/.test(v), "YYYY-MM-DD or empty"),
  monthly_rent_pounds: z.coerce.number().positive("Must be > 0"),
  rent_due_day: z.coerce.number().int().min(1).max(28),
  deposit_amount_pounds: z.coerce.number().nonnegative(),
  rent_account_name: z.string().trim().min(1).max(120),
  rent_sort_code: z
    .string()
    .trim()
    .regex(/^\d{2}-\d{2}-\d{2}$/, "NN-NN-NN"),
  rent_account_number: z
    .string()
    .trim()
    .regex(/^\d{8}$/, "8 digits"),
  rent_payment_reference: z.string().trim().min(1).max(60),
});
type FormValues = z.infer<typeof formSchema>;

function poundsToPence(p: number): number {
  return Math.round(p * 100);
}

export function AdminTenancies() {
  const [properties, setProperties] = useState<Property[]>([]);
  const [tenancies, setTenancies] = useState<TenancyRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<TenancyRow | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    const supabase = getSupabase();
    // tenancies.tenant_user_id and tenant_profiles.user_id both reference
    // auth.users separately, so PostgREST can't auto-resolve a
    // tenant_profile:tenant_profiles(...) embed. Pull the two tables in
    // parallel and merge in JS.
    const [props, tens] = await Promise.all([
      supabase.from("properties").select("*").order("display_name"),
      supabase
        .from("tenancies")
        .select(
          "id, property_id, tenant_user_id, start_date, end_date, monthly_rent_pence, status, hmf_tenant_id, property:properties(display_name)",
        )
        .order("start_date", { ascending: false }),
    ]);
    if (props.error) setError(props.error.message);
    if (tens.error) setError(tens.error.message);

    const tenanciesData =
      (tens.data as unknown as Array<Omit<TenancyRow, "tenant_profile">> | null) ?? [];
    let profilesByUser = new Map<
      string,
      { full_name: string | null; contact_email: string | null }
    >();
    if (tenanciesData.length > 0) {
      const userIds = Array.from(new Set(tenanciesData.map((t) => t.tenant_user_id)));
      const { data: profiles } = await supabase
        .from("tenant_profiles")
        .select("user_id, full_name, contact_email")
        .in("user_id", userIds);
      profilesByUser = new Map(
        (
          (profiles as Array<{
            user_id: string;
            full_name: string | null;
            contact_email: string | null;
          }> | null) ?? []
        ).map((p) => [p.user_id, { full_name: p.full_name, contact_email: p.contact_email }]),
      );
    }

    setProperties((props.data as Property[] | null) ?? []);
    setTenancies(
      tenanciesData.map((t) => ({
        ...t,
        tenant_profile: profilesByUser.get(t.tenant_user_id) ?? null,
      })),
    );
    setLoading(false);
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  if (loading) return <LoadingPanel />;

  return (
    <div className="flex flex-col gap-6">
      {error ? (
        <p role="alert" className="text-sm text-red-300">
          {error}
        </p>
      ) : null}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold tracking-tight">Tenancies</h2>
        <Button size="sm" onClick={() => setShowForm((v) => !v)}>
          <Plus size={14} />
          {showForm ? "Cancel" : "New tenancy"}
        </Button>
      </div>
      {showForm ? (
        <TenancyForm
          properties={properties}
          onSaved={() => {
            setShowForm(false);
            void refresh();
          }}
        />
      ) : null}
      {tenancies.length === 0 ? (
        <p className="text-sm text-ink-dim">No tenancies yet.</p>
      ) : (
        <ul className="grid gap-3 sm:grid-cols-2">
          {tenancies.map((t) => (
            <li key={t.id}>
              <Card>
                <CardHeader>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <CardTitle>{t.property?.display_name ?? "Unknown property"}</CardTitle>
                      <CardDescription>
                        {t.tenant_profile?.full_name ??
                          t.tenant_profile?.contact_email ??
                          "(unnamed)"}{" "}
                        · {t.status} · £{(t.monthly_rent_pence / 100).toFixed(0)}/m
                        {t.hmf_tenant_id ? ` · HMF #${t.hmf_tenant_id}` : " · NOT in HMF"}
                      </CardDescription>
                    </div>
                    <Button type="button" size="sm" variant="ghost" onClick={() => setEditing(t)}>
                      <Pencil size={14} /> Edit
                    </Button>
                  </div>
                </CardHeader>
              </Card>
            </li>
          ))}
        </ul>
      )}
      {editing ? (
        <TenancyEditor
          tenancy={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void refresh();
          }}
        />
      ) : null}
    </div>
  );
}

function TenancyForm({
  properties,
  onSaved,
}: {
  properties: Property[];
  onSaved: () => void;
}) {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      rent_due_day: 1,
      rent_account_name: "Trinium Property Management Ltd",
    },
  });
  const [globalError, setGlobalError] = useState<string | null>(null);
  const [globalNotice, setGlobalNotice] = useState<string | null>(null);

  const onSubmit = handleSubmit(async (values) => {
    setGlobalError(null);
    setGlobalNotice(null);
    const supabase = getSupabase();
    const { data, error } = await supabase.functions.invoke<{
      tenancy_id: string;
      hmf_tenant_id: number;
      invited: boolean;
    }>("admin-create-tenancy", {
      body: {
        property_id: values.property_id,
        tenant_first_name: values.tenant_first_name,
        tenant_last_name: values.tenant_last_name,
        tenant_email: values.tenant_email,
        tenant_phone: values.tenant_phone || undefined,
        start_date: values.start_date,
        end_date: values.end_date || null,
        monthly_rent_pence: poundsToPence(values.monthly_rent_pounds),
        rent_due_day: values.rent_due_day,
        deposit_amount_pence: poundsToPence(values.deposit_amount_pounds),
        rent_account_name: values.rent_account_name,
        rent_sort_code: values.rent_sort_code,
        rent_account_number: values.rent_account_number,
        rent_payment_reference: values.rent_payment_reference,
      },
    });
    if (error) {
      // supabase-js wraps a non-2xx edge response in a FunctionsHttpError whose
      // `.message` is generic ("...non-2xx status code"). The actual, useful
      // message (e.g. "This email is already registered in Help me Fix") is in
      // the response body on `.context` - read it so the admin sees the reason.
      let msg = error.message ?? "Could not create tenancy";
      const ctx = (error as { context?: Response }).context;
      if (ctx && typeof ctx.json === "function") {
        try {
          const parsed = (await ctx.json()) as { error?: string };
          if (parsed?.error) msg = parsed.error;
        } catch {
          /* keep generic message */
        }
      }
      setGlobalError(msg);
      return;
    }
    if (!data) {
      setGlobalError("Empty response");
      return;
    }
    setGlobalNotice(
      data.invited
        ? `Tenancy created. Invite email sent to the tenant. HMF tenant #${data.hmf_tenant_id}.`
        : `Tenancy created. Tenant already had an account. HMF tenant #${data.hmf_tenant_id}.`,
    );
    setTimeout(onSaved, 1500);
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>New tenancy</CardTitle>
        <CardDescription>
          Invites the tenant by email, syncs them to Help me Fix, and creates the tenancy.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form className="grid sm:grid-cols-2 gap-4" onSubmit={onSubmit} noValidate>
          <Field
            id="property_id"
            label="Property"
            error={errors.property_id?.message}
            className="sm:col-span-2"
          >
            <select
              id="property_id"
              {...register("property_id")}
              className="h-11 w-full rounded-xl border border-line bg-bg-card px-3.5 text-base md:text-[15px] text-ink"
            >
              <option value="">Select a property…</option>
              {properties.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.display_name}
                </option>
              ))}
            </select>
          </Field>

          <Field
            id="tenant_first_name"
            label="Tenant first name"
            error={errors.tenant_first_name?.message}
          >
            <Input id="tenant_first_name" {...register("tenant_first_name")} />
          </Field>
          <Field
            id="tenant_last_name"
            label="Tenant last name"
            error={errors.tenant_last_name?.message}
          >
            <Input id="tenant_last_name" {...register("tenant_last_name")} />
          </Field>
          <Field id="tenant_email" label="Tenant email" error={errors.tenant_email?.message}>
            <Input id="tenant_email" type="email" {...register("tenant_email")} />
          </Field>
          <Field
            id="tenant_phone"
            label="Tenant phone (optional)"
            error={errors.tenant_phone?.message}
          >
            <Input id="tenant_phone" placeholder="+44…" {...register("tenant_phone")} />
          </Field>

          <Field id="start_date" label="Start date" error={errors.start_date?.message}>
            <Input id="start_date" type="date" {...register("start_date")} />
          </Field>
          <Field id="end_date" label="End date (optional)" error={errors.end_date?.message}>
            <Input id="end_date" type="date" {...register("end_date")} />
          </Field>

          <Field
            id="monthly_rent_pounds"
            label="Monthly rent (£)"
            error={errors.monthly_rent_pounds?.message}
          >
            <Input
              id="monthly_rent_pounds"
              type="number"
              step="1"
              inputMode="numeric"
              {...register("monthly_rent_pounds")}
            />
          </Field>
          <Field id="rent_due_day" label="Rent due day (1-28)" error={errors.rent_due_day?.message}>
            <Input
              id="rent_due_day"
              type="number"
              min="1"
              max="28"
              inputMode="numeric"
              {...register("rent_due_day")}
            />
          </Field>
          <Field
            id="deposit_amount_pounds"
            label="Deposit (£)"
            error={errors.deposit_amount_pounds?.message}
            className="sm:col-span-2"
          >
            <Input
              id="deposit_amount_pounds"
              type="number"
              step="1"
              inputMode="numeric"
              {...register("deposit_amount_pounds")}
            />
          </Field>

          <Field
            id="rent_account_name"
            label="Account name"
            error={errors.rent_account_name?.message}
            className="sm:col-span-2"
          >
            <Input id="rent_account_name" {...register("rent_account_name")} />
          </Field>
          <Field id="rent_sort_code" label="Sort code" error={errors.rent_sort_code?.message}>
            <Input id="rent_sort_code" placeholder="NN-NN-NN" {...register("rent_sort_code")} />
          </Field>
          <Field
            id="rent_account_number"
            label="Account number"
            error={errors.rent_account_number?.message}
          >
            <Input
              id="rent_account_number"
              placeholder="12345678"
              inputMode="numeric"
              {...register("rent_account_number")}
            />
          </Field>
          <Field
            id="rent_payment_reference"
            label="Payment reference"
            error={errors.rent_payment_reference?.message}
            className="sm:col-span-2"
          >
            <Input id="rent_payment_reference" {...register("rent_payment_reference")} />
          </Field>

          {globalError ? (
            <p role="alert" className="text-xs text-red-300 sm:col-span-2">
              {globalError}
            </p>
          ) : null}
          {globalNotice ? (
            <p role="status" className="text-xs text-emerald-300 sm:col-span-2">
              {globalNotice}
            </p>
          ) : null}
          <div className="sm:col-span-2 flex">
            <Button type="submit" disabled={isSubmitting}>
              <Save size={14} />
              {isSubmitting ? "Creating…" : "Create tenancy & invite tenant"}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

type TenancyDoc = {
  name: string;
  path: string;
  size: number;
  updated_at: string | null;
  kind: "ast" | "doc";
  display_name: string;
};

function TenancyEditor({
  tenancy,
  onClose,
  onSaved,
}: {
  tenancy: TenancyRow;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [startDate, setStartDate] = useState(tenancy.start_date);
  const [endDate, setEndDate] = useState(tenancy.end_date ?? "");
  const [status, setStatus] = useState<TenancyStatus>(tenancy.status);
  const [monthlyPounds, setMonthlyPounds] = useState(
    String(Math.round(tenancy.monthly_rent_pence / 100)),
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    setError(null);
    const parsed = Number.parseFloat(monthlyPounds);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      setError("Monthly rent must be a positive number.");
      return;
    }
    setSubmitting(true);
    try {
      const sb = getSupabase();
      const { error: upErr } = await sb
        .from("tenancies")
        .update({
          start_date: startDate,
          end_date: endDate || null,
          status,
          monthly_rent_pence: Math.round(parsed * 100),
        })
        .eq("id", tenancy.id);
      if (upErr) {
        setError(upErr.message);
        return;
      }
      onSaved();
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-stretch justify-end"
      onClick={onClose}
      onKeyDown={(e) => {
        if (e.key === "Escape") onClose();
      }}
    >
      <aside
        className="bg-bg w-full max-w-2xl h-full overflow-y-auto border-l border-line"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-5 border-b border-line flex items-start justify-between gap-4">
          <div>
            <span className="text-xs uppercase tracking-[0.18em] text-ink-dim">Tenancy</span>
            <h2 className="text-xl font-semibold mt-1">
              {tenancy.property?.display_name ?? "Unknown property"}
            </h2>
            <p className="text-xs text-ink-dim mt-1">
              {tenancy.tenant_profile?.full_name ??
                tenancy.tenant_profile?.contact_email ??
                "(unnamed tenant)"}
            </p>
          </div>
          <button type="button" className="text-ink-dim hover:text-ink" onClick={onClose}>
            Close
          </button>
        </div>

        <div className="px-6 py-5 flex flex-col gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Dates &amp; rent</CardTitle>
              <CardDescription>
                Tenant identity is linked at invite time and isn't editable here yet (multi-tenant
                support coming in a follow-up).
              </CardDescription>
            </CardHeader>
            <CardContent className="grid sm:grid-cols-2 gap-4">
              <Field id="edit-start" label="Start date">
                <Input
                  id="edit-start"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </Field>
              <Field id="edit-end" label="End date (blank = open)">
                <Input
                  id="edit-end"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </Field>
              <Field id="edit-status" label="Status">
                <select
                  id="edit-status"
                  className="h-11 w-full rounded-xl border border-line bg-bg-card px-3.5 text-base md:text-[15px] text-ink"
                  value={status}
                  onChange={(e) => setStatus(e.target.value as TenancyStatus)}
                >
                  <option value="upcoming">Upcoming</option>
                  <option value="active">Active</option>
                  <option value="ended">Ended</option>
                </select>
              </Field>
              <Field id="edit-rent" label="Monthly rent (£)">
                <Input
                  id="edit-rent"
                  type="number"
                  step="1"
                  min="0"
                  inputMode="numeric"
                  value={monthlyPounds}
                  onChange={(e) => setMonthlyPounds(e.target.value)}
                />
              </Field>
              {error ? (
                <p role="alert" className="text-xs text-red-300 sm:col-span-2">
                  {error}
                </p>
              ) : null}
              <div className="sm:col-span-2 flex">
                <Button type="button" onClick={save} disabled={submitting}>
                  <Save size={14} />
                  {submitting ? "Saving…" : "Save changes"}
                </Button>
              </div>
            </CardContent>
          </Card>

          <TenancyDocsCard tenancyId={tenancy.id} />
        </div>
      </aside>
    </div>
  );
}

function TenancyDocsCard({ tenancyId }: { tenancyId: string }) {
  const [docs, setDocs] = useState<TenancyDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    const sb = getSupabase();
    const [astList, docList] = await Promise.all([
      sb.storage.from("tenancy-docs").list(`${tenancyId}/ast`, { limit: 50 }),
      sb.storage.from("tenancy-docs").list(`${tenancyId}/doc`, { limit: 200 }),
    ]);
    const out: TenancyDoc[] = [];
    for (const [items, kind] of [
      [astList.data ?? [], "ast" as const],
      [docList.data ?? [], "doc" as const],
    ] as const) {
      for (const f of items) {
        if (!f.name) continue;
        out.push({
          name: f.name,
          path: `${tenancyId}/${kind}/${f.name}`,
          size: (f.metadata?.size as number | undefined) ?? 0,
          updated_at: (f.updated_at as string | null) ?? null,
          kind,
          display_name: f.name.replace(/^\d+-/, ""),
        });
      }
    }
    setDocs(out.sort((a, b) => (b.updated_at ?? "").localeCompare(a.updated_at ?? "")));
    setLoading(false);
  }, [tenancyId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function upload(file: File, kind: "ast" | "doc") {
    setError(null);
    setUploading(true);
    try {
      const sb = getSupabase();
      const safe = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
      const path = `${tenancyId}/${kind}/${Date.now()}-${safe}`;
      const { error: upErr } = await sb.storage
        .from("tenancy-docs")
        .upload(path, file, { contentType: file.type || "application/octet-stream" });
      if (upErr) {
        setError(upErr.message);
        return;
      }
      await refresh();
    } finally {
      setUploading(false);
    }
  }

  async function open(path: string) {
    const sb = getSupabase();
    const { data } = await sb.storage.from("tenancy-docs").createSignedUrl(path, 60 * 60);
    if (data?.signedUrl) window.open(data.signedUrl, "_blank", "noopener,noreferrer");
  }

  async function remove(path: string) {
    const sb = getSupabase();
    const { error: rmErr } = await sb.storage.from("tenancy-docs").remove([path]);
    if (rmErr) {
      setError(rmErr.message);
      return;
    }
    await refresh();
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Documents</CardTitle>
        <CardDescription>
          AST and supporting documents. Visible to the tenant on this tenancy.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div className="flex flex-wrap gap-2">
          <label className="inline-flex items-center gap-2 rounded-xl border border-dashed border-line bg-bg-card px-3 py-2 text-sm text-ink-dim cursor-pointer">
            <FileUp size={14} />
            <span>{uploading ? "Uploading…" : "Upload AST (single file)"}</span>
            <input
              type="file"
              accept="application/pdf,image/*"
              className="hidden"
              disabled={uploading}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void upload(f, "ast");
                e.target.value = "";
              }}
            />
          </label>
          <label className="inline-flex items-center gap-2 rounded-xl border border-dashed border-line bg-bg-card px-3 py-2 text-sm text-ink-dim cursor-pointer">
            <FileUp size={14} />
            <span>{uploading ? "Uploading…" : "Upload supporting doc"}</span>
            <input
              type="file"
              accept="application/pdf,image/*"
              className="hidden"
              disabled={uploading}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void upload(f, "doc");
                e.target.value = "";
              }}
            />
          </label>
        </div>

        {error ? (
          <p role="alert" className="text-xs text-red-300">
            {error}
          </p>
        ) : null}

        {loading ? (
          <p className="text-sm text-ink-dim">Loading…</p>
        ) : docs.length === 0 ? (
          <p className="text-sm text-ink-dim">No documents yet.</p>
        ) : (
          <ul className="divide-y divide-line border border-line rounded-xl">
            {docs.map((d) => (
              <li key={d.path} className="flex items-center justify-between gap-3 px-4 py-2.5">
                <div className="min-w-0 flex-1">
                  <button
                    type="button"
                    className="text-sm text-accent text-left underline truncate w-full"
                    onClick={() => open(d.path)}
                  >
                    {d.display_name}
                  </button>
                  <p className="text-[11px] text-ink-dim mt-0.5">
                    {d.kind === "ast" ? "AST" : "Document"}
                    {d.updated_at ? ` · uploaded ${formatDateGB(d.updated_at)}` : ""}
                    {d.size ? ` · ${(d.size / 1024).toFixed(0)} KB` : ""}
                  </p>
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    if (confirm(`Delete ${d.display_name}? This can't be undone.`)) {
                      void remove(d.path);
                    }
                  }}
                >
                  <Trash2 size={14} />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
